import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import { stepsComponents } from '../../survey/stepsComponents';
// import DebugViewportPanel from '../debugViewportPanelV2';
// import DebugViewportPanel from '../debugViewportPanel';
import useViewportUnits from '../../utils/scripts/useViewportUnits';
import s from './index.module.scss';

const FULL_WIDTH_STEPS = new Set([
  'customV1_startPage',
  'customV2_startPage',
  'customV2_policyPage',
  'customV2_policyPageV2',
  'customV2_policyPageV3',
  'customV2_policyPageV4',
  'customV2_videoPolicyPage',
  'customV2_start_page_video',
  'customV2_startPageV2',
  'customV2_start_page_video_v2',
  'customV2_startPageV3',
  'customV2_startPageV4',
  'customV2_startPageV5',
  'customV2_startPageV6',
  'customV2_startPageV7',
  'customV2_startPageV8',
  'customV2_startPageV11',
  'customV2_startPageV12',
  'customV2_startPageV13',
  'customV2_startPageV17',
  'customV2_startPageV18',
  'customV2_startPageV19',
  'customV2_startPageV20',
  'customV2_startPageV21',
  'customV2_startPageV22',
  'customV2_startPageV23',
  'customV2_startPageV24',
  'customV2_startPageV25',
  'customV2_startPageV26',
]);

const Steps = React.memo(
  ({
    className, prev, current, next, showVisibleOverflow, 
  }) => {
    const currentWrapperStyle = useMemo(
      () => (showVisibleOverflow ? { overflow: 'visible' } : undefined),
      [showVisibleOverflow],
    );

    return (
      <div className={className}>
        {prev && stepsComponents[prev.name] && (
          <div className={`${s.stepWrapper} ${s.previousStepWrapper}`} key={`prev:${prev.name}`}>
            {stepsComponents[prev.name](prev)}
          </div>
        )}

        {current && stepsComponents[current.name] && (
          <div
            id="stepWrapperId"
            style={currentWrapperStyle}
            className={`${s.stepWrapper} ${s.currentStepWrapper}`}
            key={`current:${current.name}`}
          >
            {stepsComponents[current.name](current)}
          </div>
        )}

        {next && stepsComponents[next.name] && (
          <div className={`${s.stepWrapper} ${s.nextStepWrapper}`} key={`next:${next.name}`}>
            {stepsComponents[next.name](next)}
          </div>
        )}
        {/* <DebugViewportPanel /> */}
      </div>
    );
  },
  (prevProps, nextProps) => {
    const sameClass = prevProps.className === nextProps.className;
    const sameOverflow = prevProps.showVisibleOverflow === nextProps.showVisibleOverflow;

    const prevNamePrev = prevProps.prev?.name ?? null;
    const prevNameNext = nextProps.prev?.name ?? null;

    const currNamePrev = prevProps.current?.name ?? null;
    const currNameNext = nextProps.current?.name ?? null;

    const nextNamePrev = prevProps.next?.name ?? null;
    const nextNameNext = nextProps.next?.name ?? null;

    return (
      sameClass
      && sameOverflow
      && prevNamePrev === prevNameNext
      && currNamePrev === currNameNext
      && nextNamePrev === nextNameNext
    );
  },
);

function StepsContent() {
  const {
    stepsList, currentStepIndex, previousStep, nextStep, currentStep, 
  } = useSelector(
    (state) => ({
      stepsList: state.steps.list || null,
      previousStep: state.steps.previousStep,
      nextStep: state.steps.nextStep,
      currentStepIndex: state.steps.currentStepIndex,
      currentStep: state.steps.currentStep,
    }),
    shallowEqual,
  );

  const [isWideScreen, setIsWideScreen] = useState(() => (typeof window !== 'undefined' ? window.innerWidth >= 480 : false));

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;

    let frame = 0;
    const mq = window.matchMedia('(min-width: 480px)');

    const apply = () => {
      frame = 0;
      setIsWideScreen(mq.matches);
    };

    const onChange = () => {
      if (frame) return;
      frame = requestAnimationFrame(apply);
    };

    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else mq.addListener(onChange);

    setIsWideScreen(mq.matches);

    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else mq.removeListener(onChange);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  const list = stepsList || [];
  const safeIndex = Number.isInteger(currentStepIndex) ? currentStepIndex : 0;

  useEffect(() => {
    const stepWrapperCurrent = document.querySelector('#stepWrapperId');
    useViewportUnits(stepWrapperCurrent, { extraBottomPx: 182 });
  }, [currentStepIndex]);

  const prevIdx = useMemo(
    () => (previousStep ? safeIndex - 1 : null),
    [previousStep, safeIndex],
  );
  const nextIdx = useMemo(
    () => (nextStep ? safeIndex + 1 : null),
    [nextStep, safeIndex],
  );

  const { prev, current, next } = useMemo(() => {
    const safe = (idx) => (idx !== null && idx >= 0 && idx < list.length ? list[idx] : null);
    return {
      prev: safe(prevIdx),
      current: list[safeIndex] || null,
      next: safe(nextIdx),
    };
  }, [list, safeIndex, prevIdx, nextIdx]);

  const className = useMemo(() => {
    const isFull = FULL_WIDTH_STEPS.has(currentStep);
    return `${s.stepsContent} ${isFull ? s.fullWidth : ''}`.trim();
  }, [currentStep]);

  const showVisibleOverflow = useMemo(
    () => currentStep === 'potty_how_long' && isWideScreen,
    [currentStep, isWideScreen],
  );

  if (!current) return null;

  return (
    <Steps
      className={className}
      prev={prev}
      current={current}
      next={next}
      showVisibleOverflow={showVisibleOverflow}
    />
  );
}

export default StepsContent;
